/**
 * AdminOutreachTemplates — copy-ready message templates for founding-user
 * beta invites. Sits inside the admin console at
 * /v3/admin/console/outreach-templates.
 *
 * Every template has a "Copy" button that grabs the raw text (with
 * placeholders like [First Name] intact) so Tracy can paste into email,
 * SMS, LinkedIn DMs, etc. without any AI/CMS-style formatting getting in
 * the way.
 */
import { useState } from "react";
import { toast } from "sonner";
import {
  Copy, Check, Mail, Phone, Briefcase, RefreshCw, MessageSquare,
  Rocket, Target, Sparkles,
} from "lucide-react";

const TEMPLATES = [
  {
    id: "warm-intro-email",
    icon: Mail,
    color: "emerald",
    category: "Email · Warm intro",
    title: "For people who know you personally",
    subjectLines: [
      "Quick favor — building something you might actually use",
      "Beta invite (only sending this to 5 people)",
      "You know my deal flow — I built the tool I wish existed",
    ],
    body: `Hey [First Name],

Quick one — I've been quietly building something for the last few months that I think you're going to love, and I want you to be one of the first 5 people to see it before I open it up.

You know how much time we all bleed on the same 3 things:
- Pulling comps manually and second-guessing ARV
- Guessing at rehab numbers instead of building them from real line items
- Chasing signed contracts across email, texts, and DocuSign chaos

I built iSpeedToClose to fix all three in one place. It's a real-estate operating system — properties are the center, everything else (comps, repair estimates, contracts, cash buyers, AI voice) revolves around them. Not another CRM. An actual workflow.

What I need from you:
1. 15 minutes to run one of your actual deals through it (any property you've analyzed recently)
2. Brutal honest feedback — what's confusing, what's slow, what's missing
3. If it saves you time, tell me. If it doesn't, tell me that too.

What you get:
- Free lifetime access (locked in as a First Closer)
- Direct line to me for any changes you want made
- Your logo/branding on the DocuSign contracts you send

Interested? Reply "yes" and I'll send you a login within 5 minutes.

Tracy`,
  },
  {
    id: "sms-first-touch",
    icon: Phone,
    color: "blue",
    category: "SMS · First touch",
    title: "Casual gauge-of-interest before dropping the full email",
    body: `Hey [Name] — quick one. I've been building a tool for RE investors like us. Real workflow, not another lipstick-on-a-CRM. Want to be one of 5 people to try it before I open it up? Free forever if you help me stress-test it. Yes or no?`,
  },
  {
    id: "formal-cold-email",
    icon: Briefcase,
    color: "violet",
    category: "Email · Slightly formal",
    title: "Bigger operators or less-personal contacts",
    subjectLines: [
      "Founding-user invite: iSpeedToClose (real-estate operating system)",
    ],
    body: `Hi [Name],

[Name of mutual connection] mentioned you're doing volume in [market] — congrats on that.

Reason I'm reaching out: I've spent the last few months building a tool that consolidates the fragmented stack most of us use for deal analysis + contract execution. It's called iSpeedToClose.

What it does:
- Property-first architecture — everything (comps, repairs, contracts, buyers, AI voice screening) revolves around the property, not around your inbox
- AI-powered deal scoring across 5 dimensions with real ARV/repair math
- Instant strategy comparison (Fix & Flip vs BRRRR vs Wholetail vs Assign vs Sub-To vs Seller Finance)
- Shareable pitch pages that turn every deal into a link you can text to cash buyers
- DocuSign-integrated contract execution with per-user OAuth
- Twilio voice AI ("Apex") screens seller callbacks in your voice/brand

I'm inviting 5 operators to be founding beta users before we open it up. In exchange for using it on 1-3 real deals over the next 30 days and sharing feedback, you get free lifetime access and a spot on the launch roster.

If you're interested, reply here and I'll set you up in 10 minutes.

Best,
Tracy Perrin
Perrin Properties, LLC
tperrin@perrinweb.com | https://ispeedtoclose.com`,
  },
  {
    id: "followup-day3",
    icon: RefreshCw,
    color: "amber",
    category: "Follow-up · Day 3",
    title: "No response yet",
    body: `Hey [Name] — did the earlier note get buried? No stress if not interested. If you are, I'll hold your beta spot until Friday.`,
  },
  {
    id: "followup-inactive",
    icon: RefreshCw,
    color: "amber",
    category: "Follow-up · Signed up but hasn't logged in",
    title: "They said yes but haven't logged in yet",
    body: `[Name] — noticed you haven't logged in yet. Want me to jump on a 10-min screen share and walk you through it on one of your actual deals? Free, no pitch, just want you to see it work.`,
  },
  {
    id: "checkin-active",
    icon: MessageSquare,
    color: "emerald",
    category: "Check-in · 3 days after activation",
    title: "They're using it — get feedback",
    body: `How's it feeling so far? What tripped you up? What worked? Anything you wish it did that it doesn't? All feedback lands directly on my desk.`,
  },
];

const STRATEGY = [
  { day: "Day 1", action: "5 SMS blasts to your closest RE contacts", template: "sms-first-touch" },
  { day: "Day 2", action: "Full email to anyone who said yes", template: "warm-intro-email" },
  { day: "Day 3", action: "Cold-ish email 10 more (bigger operators)", template: "formal-cold-email" },
  { day: "Day 5", action: "Public LinkedIn post with workspace screenshot", template: null },
];

const COLOR_STYLES = {
  emerald: "bg-emerald-100 text-emerald-700",
  blue: "bg-blue-100 text-blue-700",
  violet: "bg-violet-100 text-violet-700",
  amber: "bg-amber-100 text-amber-700",
};

function CopyButton({ text, testId }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(
      () => {
        setCopied(true);
        toast.success("Copied to clipboard");
        setTimeout(() => setCopied(false), 2000);
      },
      () => toast.error("Copy failed — long-press to select text manually.")
    );
  };
  return (
    <button
      onClick={copy}
      className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold transition-colors ${
        copied
          ? "bg-emerald-600 text-white"
          : "bg-slate-900 text-white hover:bg-slate-700"
      }`}
      data-testid={testId}
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function TemplateCard({ template }) {
  const Icon = template.icon;
  return (
    <div
      className="rounded-xl border border-slate-200 bg-white overflow-hidden"
      data-testid={`template-${template.id}`}
    >
      <div className="flex items-start gap-3 px-5 py-4 border-b border-slate-100 bg-slate-50/50">
        <div className={`p-2 rounded-lg shrink-0 ${COLOR_STYLES[template.color] || COLOR_STYLES.emerald}`}>
          <Icon size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
            {template.category}
          </div>
          <div className="text-sm font-semibold text-slate-900 mt-0.5">
            {template.title}
          </div>
        </div>
      </div>

      {template.subjectLines && (
        <div className="px-5 py-3 border-b border-slate-100">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Subject lines (pick one)</div>
          <div className="space-y-1.5">
            {template.subjectLines.map((s, i) => (
              <div key={i} className="flex items-center justify-between gap-3">
                <code className="flex-1 text-xs text-slate-800 bg-slate-100 rounded px-2 py-1 truncate">
                  {s}
                </code>
                <CopyButton text={s} testId={`copy-subject-${template.id}-${i}`} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="px-5 py-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Body</div>
          <CopyButton text={template.body} testId={`copy-body-${template.id}`} />
        </div>
        <pre className="text-[13px] leading-relaxed text-slate-800 whitespace-pre-wrap font-sans bg-slate-50/40 border border-slate-100 rounded-md p-3">
          {template.body}
        </pre>
      </div>
    </div>
  );
}

export default function AdminOutreachTemplates() {
  return (
    <div className="space-y-6" data-testid="admin-outreach-templates">
      {/* Hero */}
      <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-900 to-slate-800 text-white p-6 md:p-8">
        <div className="flex items-start gap-3">
          <div className="p-2.5 rounded-lg bg-emerald-500/20 text-emerald-400">
            <Rocket size={22} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold">
              Founding-user outreach
            </div>
            <h1 className="text-2xl md:text-3xl font-black tracking-tight mt-1">
              Get your first 5 beta users
            </h1>
            <p className="text-sm text-slate-300 mt-2 max-w-2xl leading-relaxed">
              Copy-ready templates crafted in your voice. Personalize the [brackets],
              send in the order below, and expect 5-10 active users within 10 days.
            </p>
          </div>
        </div>
      </div>

      {/* Strategy timeline */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 md:p-6">
        <div className="flex items-center gap-2 mb-4">
          <Target size={16} className="text-emerald-600" />
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">
            Distribution strategy
          </h2>
        </div>
        <div className="grid gap-3 md:grid-cols-4">
          {STRATEGY.map((s, i) => (
            <div key={i} className="rounded-lg border border-slate-200 p-3">
              <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-600">
                {s.day}
              </div>
              <div className="text-[13px] text-slate-800 font-medium mt-1 leading-snug">
                {s.action}
              </div>
              {s.template && (
                <a
                  href={`#template-${s.template}`}
                  className="mt-2 inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-900"
                >
                  Jump to template →
                </a>
              )}
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-start gap-2 text-xs text-slate-600 bg-emerald-50 border border-emerald-200 rounded-md p-3">
          <Sparkles size={14} className="text-emerald-600 mt-0.5 shrink-0" />
          <div>
            <div className="font-bold text-slate-900">Target: 5-10 active beta users in first 10 days.</div>
            <div className="mt-0.5 text-slate-600">
              Once DocuSign Go-Live is approved, start Day 1. Move fast — every real user is worth 100× a new feature.
            </div>
          </div>
        </div>
      </div>

      {/* Templates */}
      <div className="space-y-4">
        {TEMPLATES.map((t) => (
          <div key={t.id} id={`template-${t.id}`}>
            <TemplateCard template={t} />
          </div>
        ))}
      </div>

      {/* Voice check */}
      <div className="rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 p-5 md:p-6">
        <div className="text-sm font-bold text-slate-900 mb-1">💡 Pro tip before you hit send</div>
        <p className="text-sm text-slate-600 leading-relaxed">
          Read the message out loud. If it sounds like <em>you</em>, send it.
          If it sounds like &quot;generic SaaS founder,&quot; tweak until it doesn&apos;t.
          Your authentic voice is your unfair advantage.
        </p>
      </div>
    </div>
  );
}
